<template>
  <section class="dc-zone">
    <v-btn text class="mb-3" data-testid="back-to-zones" :disabled="reviewBusy" @click="$emit('close')">{{ $t('damage_control.capture.back') }}</v-btn>
    <h2 class="text-h6">{{ zone.title }}</h2>
    <p class="mt-2">{{ zone.hint }}</p>
    <p data-testid="attempt">{{ $t('damage_control.capture.attempt', { attempt: displayedAttempt }) }}</p>
    <v-alert v-if="!availability.available" type="info" outlined data-testid="camera-unavailable">
      {{ $t('damage_control.capture.camera_unavailable') }}
    </v-alert>
    <v-alert v-if="errorText" type="warning" outlined role="alert" data-testid="capture-error">{{ errorText }}</v-alert>
    <v-alert v-if="lowResolution" data-testid="low-resolution" type="warning" outlined>{{ $t('damage_control.capture.low_resolution') }}</v-alert>
    <v-alert v-if="restoredCapture && editable" type="info" outlined data-testid="restored-photo">
      {{ $t('damage_control.capture.restored_photo') }}
      <v-btn text data-testid="use-restored-photo" :disabled="busy" @click="useRestoredPhoto">{{ $t('damage_control.capture.use_restored_photo') }}</v-btn>
      <v-btn text data-testid="discard-restored-photo" :disabled="busy" @click="discardRestoredPhoto">{{ $t('damage_control.capture.retake') }}</v-btn>
    </v-alert>
    <div v-if="mode === 'camera' && availability.available" class="dc-viewfinder">
      <video v-if="availability.kind === 'web'" ref="video" autoplay muted playsinline />
      <img v-if="referenceUrl" :src="referenceUrl" :alt="$t('damage_control.capture.reference_photo')"
           :class="{ 'dc-ghost': availability.kind === 'web' }" @error="refreshPreview(referenceUrl)" />
    </div>
    <div v-if="mode !== 'camera' && (!currentShot || (attemptShot && attemptShot.id !== currentShot.id))" class="dc-comparison">
      <figure v-if="displayPreviewUrl">
        <img @error="refreshPreview(displayPreviewUrl)" :src="displayPreviewUrl" :alt="$t('damage_control.capture.current_photo')" />
        <figcaption>{{ $t('damage_control.capture.current_photo') }}</figcaption>
      </figure>
    </div>
    <p v-if="mode !== 'camera'" role="status" data-testid="shot-status">{{ $t(stateKey) }}</p>
    <ul v-if="reasonKeys.length" class="mb-4">
      <li v-for="key in reasonKeys" :key="key">{{ $t(key) }}</li>
    </ul>
    <!-- API: проверка качества может отказать без названной причины (провал порога годности). -->
    <p v-if="rejected && !reasonKeys.length" class="mb-4" data-testid="rejected-generic">{{ $t('damage_control.capture.rejected_generic') }}</p>
    <p v-if="uploading" role="status" data-testid="uploading">{{ $t('damage_control.capture.uploading') }}</p>
    <v-progress-linear v-if="busy" data-testid="capture-progress" indeterminate class="mb-4" />
    <div class="dc-actions">
      <v-btn v-if="availability.available && mode === 'camera' && editable && !restoredCapture" color="primary" data-testid="take-photo"
             :disabled="busy || (availability.kind === 'web' && !streamReady)" @click="takePhoto">
        {{ $t('damage_control.capture.take_photo') }}
      </v-btn>
      <v-btn v-if="availability.available && mode === 'result' && canRetake" color="primary" data-testid="retake"
             :disabled="busy" @click="beginCapture">
        {{ $t(currentShot && !rejected ? 'damage_control.capture.retake_zone' : 'damage_control.capture.retake') }}
      </v-btn>
      <v-btn data-testid="retry-upload" v-if="candidate && uploadFailed && editable" color="primary" :disabled="busy" @click="sendCandidate">
        {{ $t('damage_control.capture.retry_upload') }}
      </v-btn>
      <v-btn v-if="needsRefresh" outlined :disabled="busy" data-testid="refresh" @click="refreshVerdict">
        {{ $t('damage_control.capture.refresh') }}
      </v-btn>
    </div>
    <shot-review v-if="currentShot && mode !== 'camera'" :key="currentShot.id" :shot="currentShot"
                 :inspection="localInspection" :zone="activeZone" :entries="entries"
                 :preview-url="previewUrl" :departure-preview-url="departurePreviewUrl"
                 @preview-error="refreshPreview" @busy="reviewBusy = $event" @drawing="drawing = $event" @update:inspection="receiveInspection" />

    <zone-review-control v-if="!currentShot" :inspection="localInspection" :zone="activeZone" @update:inspection="receiveInspection" />
  </section>
</template>

<script>
import { reportDamageControlFailure } from "@/api/damageControl";
import { cameraAvailability, captureNative, openWebStream, captureFrame, stopStream } from "@/services/damageControlCamera";
import { analyzeBlob, makePreview, decodeOriented } from "@/utils/damageControlQuality";
import { uploadShot, nextSeriesAttempt, shouldUploadAttempt, pollInspection } from "@/services/damageControlShots";
import { shouldRefreshDamageControlPreview } from "@/utils/damageControlPreview";
import { damageControlAttempts } from "@/services/damageControlAttempts";
import { peekRestoredDamageControl, takeRestoredDamageControl, discardRestoredDamageControl } from "@/services/damageControlRecovery";
import ShotReview from "../review/ShotReview.vue";
import ZoneReviewControl from "../review/ZoneReviewControl.vue";
import { STATE_KEYS, reasonKeys, acceptedShot, confirmedFindings, observedShot } from "./presentation";

const defaults = { cameraAvailability, captureNative, openWebStream, captureFrame, stopStream, analyzeBlob, makePreview, decodeOriented, uploadShot, pollInspection };

export default {
  name: "ZoneCapture",
  components: { ShotReview, ZoneReviewControl },
  props: {
    inspection: { type: Object, required: true },
    zone: { type: Object, required: true },
    entries: { type: Array, default: () => [] },
    history: { type: Array, default: () => [] },
    previewUrl: { type: String, default: "" },
    departurePreviewUrl: { type: String, default: "" },
    ghostUrl: { type: String, default: "" },
    // Native/browser capabilities are injectable, without requiring a real camera in tests.
    cameraDeps: { type: Object, default: () => ({}) },
    services: { type: Object, default: () => ({}) },
  },
  data() {
    this.history.forEach(shot => damageControlAttempts.remember(this.inspection.id, this.zone.key, shot));
    const history = damageControlAttempts.history(this.inspection.id, this.zone.key, this.zone.latest_shot);
    const last = history[history.length - 1];
    const shot = observedShot(this.zone, last || null);
    return {
      localInspection: this.inspection, localHistory: history, attemptShot: shot,
      mode: shot || this.zone.state === "pending" ? "result" : "camera",
      availability: { available: false, kind: null }, stream: null, streamReady: false,
      busy: false, polling: false, reviewBusy: false, drawing: false, uploading: false, errorText: "", lowResolution: false,
      candidate: null, candidateUrl: "", uploadFailed: false, needsRefresh: false,
      controller: null, warningController: null, disposed: false, failedPreviewUrls: [],
      restoredCapture: peekRestoredDamageControl(this.inspection.id, this.zone.key),
    };
  },
  computed: {
    io() { return { ...defaults, ...this.services }; },
    activeZone() { return (this.localInspection.zones || []).find(zone => zone.key === this.zone.key) || this.zone; },
    currentShot() { return this.activeZone.current_shot || null; },
    editable() { return this.localInspection.status === "in_progress"; },
    referenceUrl() { return this.ghostUrl || this.activeZone.example_photo_url || ""; },
    nextAttempt() {
      if (this.attemptShot && this.attemptShot.state === "pending") return { series: this.attemptShot.series, attempt: this.attemptShot.attempt };
      return nextSeriesAttempt(this.activeZone, this.localHistory);
    },
    displayedAttempt() { return this.mode === "camera" ? this.nextAttempt.attempt : (this.attemptShot && this.attemptShot.attempt) || this.nextAttempt.attempt; },
    rejected() { return this.attemptShot && this.attemptShot.state === "rejected"; },
    stateKey() { return STATE_KEYS[(this.attemptShot && this.attemptShot.state) || this.activeZone.state] || STATE_KEYS.missing; },
    reasonKeys() { return reasonKeys(this.attemptShot); },
    canRetake() { return !this.reviewBusy && !this.drawing && this.editable && !this.uploadFailed && !this.needsRefresh && (!this.attemptShot || this.attemptShot.state !== "pending") && this.activeZone.state !== "pending"; },
    interactionBusy() { return (this.busy && !this.polling) || this.reviewBusy || this.drawing; },
    displayPreviewUrl() { return this.candidateUrl || this.previewUrl; },
  },
  watch: {
    inspection(value) {
      this.localInspection = value;
      this.attemptShot = observedShot(this.activeZone, this.attemptShot);
      if (this.attemptShot) this.recordAttempt(this.attemptShot);
    },
    interactionBusy(value) { this.$emit("busy", value); },
  },
  async mounted() {
    this.availability = this.io.cameraAvailability(this.cameraDeps);
    if (this.mode === "camera" && this.editable && !this.restoredCapture) await this.startStream();
    else if ((this.attemptShot && this.attemptShot.state === "pending") || this.activeZone.state === "pending" || (this.currentShot && this.currentShot.vision_stage === "pending")) await this.refreshVerdict();
  },
  beforeDestroy() {
    this.disposed = true;
    if (this.controller) this.controller.abort();
    if (this.warningController) this.warningController.abort();
    this.releaseStream();
    this.releasePreview();
  },
  methods: {
    refreshPreview(url) {
      if (shouldRefreshDamageControlPreview(this.failedPreviewUrls, url)) this.$emit("preview-error", url);
    },
    releaseStream() {
      this.io.stopStream(this.stream);
      this.stream = null;
      this.streamReady = false;
    },
    releasePreview() {
      if (this.candidateUrl) URL.revokeObjectURL(this.candidateUrl);
      this.candidateUrl = "";
    },
    async startStream() {
      if (!this.availability.available || this.availability.kind !== "web" || this.disposed) return;
      try {
        const stream = await this.io.openWebStream(this.cameraDeps);
        if (this.disposed || this.mode !== "camera") { this.io.stopStream(stream); return; }
        this.stream = stream;
        await this.$nextTick();
        const video = this.$refs.video;
        if (video) {
          video.srcObject = stream;
          video.onloadedmetadata = () => { if (!this.disposed && this.stream === stream) this.streamReady = video.videoWidth > 0; };
          await video.play();
          this.streamReady = video.videoWidth > 0;
        }
      } catch (error) {
        this.releaseStream();
        if (!this.disposed) this.errorText = this.$t("damage_control.capture.camera_failed");
      }
    },
    async enterCamera() {
      this.mode = "camera";
      await this.$nextTick();
      await this.startStream();
    },
    async beginCapture() {
      if (!this.canRetake || this.busy) return;
      if (confirmedFindings(this.currentShot)) {
        this.errorText = this.$t("damage_control.capture.reshoot_blocked");
        return;
      }
      if (this.warningController) this.warningController.abort();
      this.errorText = "";
      this.lowResolution = false;
      this.candidate = null;
      this.releasePreview();
      await this.enterCamera();
    },
    recordAttempt(shot) {
      damageControlAttempts.remember(this.inspection.id, this.zone.key, shot);
      const index = this.localHistory.findIndex(item => item.series === shot.series && item.attempt === shot.attempt);
      if (index < 0) this.localHistory.push(shot);
      else this.$set(this.localHistory, index, shot);
      this.$emit("attempt", shot);
    },
    async takePhoto() {
      if (this.busy || !this.availability.available || !this.editable) return;
      this.busy = true;
      this.errorText = "";
      try {
        const sequence = this.nextAttempt;
        const original = this.availability.kind === "native"
          ? await this.io.captureNative({ ...this.cameraDeps, inspectionId: this.inspection.id, zoneKey: this.zone.key, ...sequence })
          : await this.io.captureFrame(this.$refs.video, this.cameraDeps);
        if (!original || this.disposed) return;
        await this.processPhoto(original, sequence);
      } catch (error) {
        if (!this.disposed) this.errorText = error.i18nKey ? this.$t(error.i18nKey) : this.$t("damage_control.capture.camera_failed");
      } finally { if (!this.disposed) this.busy = false; }
    },
    async useRestoredPhoto() {
      if (!this.restoredCapture || this.busy || !this.editable) return;
      this.busy = true;
      this.errorText = "";
      try {
        const recovered = await takeRestoredDamageControl(this.inspection.id, this.zone.key, this.cameraDeps);
        if (!recovered || this.disposed) return;
        this.restoredCapture = null;
        await this.processPhoto(recovered.original, { series: recovered.series, attempt: recovered.attempt });
      } catch (error) {
        if (!this.disposed) this.errorText = this.$t("damage_control.capture.camera_failed");
      } finally { if (!this.disposed) this.busy = false; }
    },
    async discardRestoredPhoto() {
      if (this.busy) return;
      discardRestoredDamageControl(this.inspection.id, this.zone.key);
      this.restoredCapture = null;
      await this.enterCamera();
    },
    async processPhoto(original, sequence) {
      const decoded = this.io.analyzeBlob === analyzeBlob && this.io.makePreview === makePreview
        ? await this.io.decodeOriented(original) : null;
      let ready;
      try { ready = await this.processDecodedPhoto(original, sequence, decoded); }
      finally { if (decoded && decoded.close) decoded.close(); }
      if (ready) await this.sendCandidate();
    },
    async processDecodedPhoto(original, sequence, decoded) {
      const quality = await this.io.analyzeBlob(original, undefined, decoded);
      if (this.disposed) return;
      this.releaseStream();
      this.lowResolution = Boolean(quality.lowResolution);
      const upload = shouldUploadAttempt({ attempt: sequence.attempt, reasons: quality.reasons });
      const attempt = { ...sequence, state: upload ? "pending" : "rejected", reason: quality.reasons };
      this.attemptShot = attempt;
      this.mode = "result";
      // Only actual device rejections spend a local attempt before registration.
      if (!upload) {
        this.recordAttempt(attempt);
        return;
      }
      let preview;
      try { preview = await this.io.makePreview(original, undefined, decoded); }
      catch (error) {
        this.attemptShot = observedShot(this.activeZone, null);
        await this.enterCamera();
        throw error;
      }
      if (this.disposed) return;
      this.releasePreview();
      this.candidateUrl = URL.createObjectURL(preview);
      this.candidate = { inspectionId: this.inspection.id, zoneKey: this.zone.key, original, preview,
        ...sequence, previousShotId: (this.activeZone.latest_shot || this.currentShot || {}).id || 0, capturedAt: original.capturedAt, reasons: quality.reasons };
      return true;
    },
    async sendCandidate() {
      if (!this.candidate || this.disposed) return;
      this.busy = true;
      this.uploading = true;
      this.uploadFailed = false;
      this.errorText = "";
      this.controller = new AbortController();
      try {
        const shot = await this.io.uploadShot(this.axios, this.candidate, { fetchImpl: this.cameraDeps.fetchImpl, signal: this.controller.signal });
        if (this.disposed) return;
        this.attemptShot = shot;
        this.recordAttempt(shot);
        this.$emit("preview", { shotId: shot.id, preview: this.candidate.preview });
        this.candidate = null;
        this.uploading = false;
        await this.waitForVerdict();
      } catch (error) {
        if (!this.disposed && error.name !== "AbortError") {
          this.uploadFailed = Boolean(this.candidate);
          this.showFailure(error);
        }
      } finally {
        if (!this.disposed) { this.busy = false; this.uploading = false; }
      }
    },
    showFailure(error) {
      this.errorText = error.i18nKey ? this.$t(error.i18nKey) : reportDamageControlFailure(this, error, { silent: true });
      this.needsRefresh = !this.candidate;
    },
    receiveInspection(payload) {
      if (this.disposed) return;
      this.localInspection = payload;
      this.attemptShot = observedShot(this.activeZone, this.attemptShot);
      if (this.attemptShot) this.recordAttempt(this.attemptShot);
      this.$emit("update:inspection", payload);
    },
    async waitForVerdict() {
      this.polling = true;
      try {
        this.needsRefresh = false;
        const options = { signal: this.controller.signal, onUpdate: this.receiveInspection };
        await this.io.pollInspection(this.axios, this.inspection.id, { ...options, timeoutMs: 15000,
          until: () => this.attemptShot ? this.attemptShot.state !== "pending" : this.activeZone.state !== "pending" });
        if (this.disposed) return;
        if (this.attemptShot && this.attemptShot.state === "accepted" && this.currentShot && this.currentShot.id !== this.attemptShot.id && confirmedFindings(this.currentShot)) {
          this.errorText = this.$t("damage_control.capture.reshoot_blocked");
        }
        if (acceptedShot(this.currentShot) && this.currentShot.vision_stage === "pending") this.pollWarnings();
      } finally { this.polling = false; }
    },
    pollWarnings() {
      if (this.warningController) this.warningController.abort();
      const controller = new AbortController();
      this.warningController = controller;
      this.io.pollInspection(this.axios, this.inspection.id, {
        signal: controller.signal, timeoutMs: 70000,
        onUpdate: payload => { if (!controller.signal.aborted) this.receiveInspection(payload); },
        until: () => !this.currentShot || this.currentShot.vision_stage !== "pending",
      }).catch(error => {
        if (!this.disposed && !controller.signal.aborted && error.name !== "AbortError") this.showFailure(error);
      });
    },
    async refreshVerdict() {
      if (this.busy || this.disposed) return;
      this.busy = true;
      this.errorText = "";
      this.controller = new AbortController();
      try { await this.waitForVerdict(); }
      catch (error) { if (!this.disposed && error.name !== "AbortError") this.showFailure(error); }
      finally { if (!this.disposed) this.busy = false; }
    },
  },
};
</script>

<style scoped>
.dc-zone { min-width: 0; }
.dc-viewfinder { position: relative; width: 100%; background: #151515; overflow: hidden; }
.dc-viewfinder video { display: block; width: 100%; max-height: 65vh; object-fit: contain; }
.dc-viewfinder img { display: block; width: 100%; max-height: 65vh; object-fit: contain; }
.dc-viewfinder .dc-ghost { position: absolute; inset: 0; height: 100%; opacity: 0.35; pointer-events: none; }
.dc-comparison { display: flex; gap: 16px; align-items: start; }
.dc-comparison figure { margin: 0; min-width: 0; flex: 1; }
.dc-comparison img { display: block; width: 100%; max-height: 60vh; object-fit: contain; }
.dc-comparison figcaption { margin: 8px 0 16px; }
.dc-actions { display: flex; flex-wrap: wrap; gap: 12px; margin: 16px 0; }
@media (max-width: 599px) {
  .dc-zone > .v-btn { width: 100%; }
  .dc-comparison { flex-direction: column; }
  .dc-comparison figure, .dc-actions > * { width: 100%; }
  .dc-actions { flex-direction: column; }
}
</style>
